import { useState, useEffect } from "react";
import { Copy, Check, Loader2, Monitor, RefreshCw, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MacOsFdaCallout } from "@/components/ui/macos-fda-callout";
import { useCreateAgentKey, useDeployAgents } from "@/hooks/use-api";
import type { AgentKeyInstall, AgentOSType } from "@/types/api";

interface AgentDeployStepProps {
  isCompleted: boolean;
}

function buildCommand(os: AgentOSType, installUrl: string): string {
  if (os === "windows") {
    return `powershell -NoProfile -ExecutionPolicy Bypass -NoExit -Command "\`$ErrorActionPreference='Stop'; try { iwr -useb '${installUrl}' | iex } catch { Write-Host ''; Write-Error \`$_ -ErrorAction Continue; Read-Host 'Press Enter to close'; exit 1 }"`;
  }
  return `curl -fsSL "${installUrl}" | sudo bash`;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard API unavailable (non-HTTPS or permission denied) — no-op
    });
  }

  return (
    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleCopy}>
      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

function InstallCommandBlock({
  os,
  install,
}: {
  os: AgentOSType;
  install: AgentKeyInstall | null;
}) {
  if (!install) {
    return (
      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-xs text-amber-700 dark:text-amber-300">
        Click Generate install command for this OS, then paste the command into the target host. No separate API key handling is needed.
      </div>
    );
  }

  const command = buildCommand(os, install.install_url);
  return (
    <div className="space-y-2">
      <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        This command already contains the one-time install token. It works once and expires at {new Date(install.expires_at).toLocaleString()} — if it 410s or fails, generate a fresh command.
      </p>
      <div className="relative rounded-md border bg-muted">
        <div className="absolute right-2 top-2">
          <CopyButton text={command} />
        </div>
        <pre className="overflow-x-auto p-4 pr-10 font-mono text-xs leading-relaxed">
          {command}
        </pre>
      </div>
    </div>
  );
}

export function AgentDeployStep({ isCompleted }: AgentDeployStepProps) {
  const [endpointOs, setEndpointOs] = useState<AgentOSType>("linux");
  const [installInfo, setInstallInfo] = useState<AgentKeyInstall | null>(null);
  const [installOs, setInstallOs] = useState<AgentOSType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createKey = useCreateAgentKey();
  const { data: agentsData, refetch: refetchAgents } = useDeployAgents();

  // Poll agents every 5s
  useEffect(() => {
    const id = setInterval(() => {
      void refetchAgents();
    }, 5000);
    return () => clearInterval(id);
  }, [refetchAgents]);

  const agentCount = agentsData?.summary.active ?? 0;
  const visibleInstall = installOs === endpointOs ? installInfo : null;

  function handleGenerateCommand() {
    setError(null);
    setInstallInfo(null);
    setInstallOs(null);
    createKey.mutate(
      {
        name: `Setup Wizard ${endpointOs} Agent`,
        purpose: "prod",
        os: endpointOs,
      },
      {
        onSuccess: (data) => {
          if (!data.install) {
            setError("Install command was not returned. Try again or contact support.");
            return;
          }
          setInstallInfo(data.install);
          setInstallOs(endpointOs);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Failed to generate install command");
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Deploy the Praying Mantis Agent</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Install the lightweight agent on your endpoints to start collecting security events.
        </p>
        <p className="mt-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Pick the host OS, generate an install command, paste it into that host, then wait for the first heartbeat. You do not need to save or paste a separate API key.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium">One-command endpoint install</p>
              <p className="text-xs text-muted-foreground">
                Each endpoint gets its own command and agent key automatically.
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleGenerateCommand}
              disabled={createKey.isPending}
            >
              {createKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Generate install command
            </Button>
          </div>
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="space-y-2">
        <p className="text-sm font-medium">Install Command</p>
        <Tabs value={endpointOs} onValueChange={(value) => setEndpointOs(value as AgentOSType)}>
          <TabsList>
            <TabsTrigger value="linux">
              <Terminal className="mr-1.5 h-3.5 w-3.5" />
              Linux
            </TabsTrigger>
            <TabsTrigger value="macos">
              <Monitor className="mr-1.5 h-3.5 w-3.5" />
              macOS
            </TabsTrigger>
            <TabsTrigger value="windows">
              <Monitor className="mr-1.5 h-3.5 w-3.5" />
              Windows
            </TabsTrigger>
          </TabsList>

          <TabsContent value="linux">
            <InstallCommandBlock os="linux" install={visibleInstall} />
          </TabsContent>

          <TabsContent value="macos">
            <p className="mb-2 text-xs text-muted-foreground">
              Requires{" "}
              <a href="https://brew.sh" target="_blank" rel="noreferrer" className="underline">
                Homebrew
              </a>
              . Install it first as your regular user. The installer uses Homebrew
              for Fluent Bit and Python 3.12, then prompts for Full Disk Access.
            </p>
            <InstallCommandBlock os="macos" install={visibleInstall} />
            <MacOsFdaCallout />
          </TabsContent>

          <TabsContent value="windows">
            <p className="mb-2 text-xs text-muted-foreground">
              Run in an elevated PowerShell terminal (Run as Administrator). This
              command keeps PowerShell open if the installer errors so you can copy
              the diagnostic instead of losing the window.
            </p>
            <InstallCommandBlock os="windows" install={visibleInstall} />
          </TabsContent>
        </Tabs>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">
          Agents connected:{" "}
          <span className="font-medium text-foreground">{agentCount}</span>
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void refetchAgents()}
          className="h-7 gap-1.5 text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>
        {isCompleted && (
          <span className="ml-auto text-xs text-green-600">Agent detected</span>
        )}
      </div>
    </div>
  );
}
